/* tslint:disable:cyclomatic-complexity */

import { CameraMessage, Marker, MarkerType, PolicyMap, TimelineBar } from 'types';

export type AccessType =
    'AccessDenied'
    | 'AccessOther'
    | 'AccessGranted';

export type TimelineBarType =
    'Stable'
    | 'CameraDownload'
    | 'NoSignal'
    | 'Motion'
    | 'Alarm'
    | 'AccessGranted'
    | 'AccessOther'
    | 'AccessDenied'
    | 'Zoom';

export interface ITimelineBarBuilder {
    type: TimelineBarType;
    zIndex: number;
    minWidth: number;
    top: number; // [0 .. 1]
    height: number; // [0 .. 1]
    start: CameraMessage;
    end: CameraMessage;

    toBar(baseTime: number, scale: number, segmentHeight: number): TimelineBar;
    getMarker(baseTime: number, scale: number): Marker | null;
}

export function generateTimelineBarBuilders(messages: readonly CameraMessage[], autoTrackingPolicies: PolicyMap): ITimelineBarBuilder[] {
    class TimelineBarBuilder implements ITimelineBarBuilder {
        public type: TimelineBarType;
        public zIndex: number;
        public minWidth: number;
        public top: number; // [0 .. 1]
        public height: number; // [0 .. 1]
        public start: CameraMessage;
        public end: CameraMessage;

        constructor(type: TimelineBarType, zIndex: number, start: CameraMessage, minWidth: number = 0, top: number = 0, height: number = 1) {
            this.type = type;
            this.zIndex = zIndex;
            this.minWidth = minWidth;
            this.top = top;
            this.height = height;
            this.start = start;
            this.end = start;
        }

        // Produce a single bar from the start and end times of the current messages.
        // The bar has a real pixel location and size.
        // - baseTime is the left-most time on the first timeline segment
        // - scale is a multiplier from UTC milliseconds to pixels
        toBar(baseTime: number, scale: number, segmentHeight: number): TimelineBar {
            // Calculate the horizontal position and width of the bar
            let x = (this.start.t - baseTime) * scale;
            const rawDuration = this.end.t - this.start.t;
            const scaledDuration = rawDuration * scale;
            let width = scaledDuration;

            // Some bars have a minimum width regardless of scale.
            // Compensate if the current bar is not wide enough.
            if (scaledDuration < this.minWidth) {
                // Expand the bar to the minimum width, centered on where the bar current sits
                x -= (this.minWidth - scaledDuration) / 2;
                width = this.minWidth;
            }

            return {
                x,
                y: segmentHeight * this.top,
                width,
                height: segmentHeight * this.height,
                type: this.type,
                // TODO: include info needed by the bar to render a label and let user interact. e.g. policy id, alarm id, times, etc.
            };
        }

        getMarker(baseTime: number, scale: number): Marker | null {
            // Only alarms and access events have a marker
            if (this.type !== 'Alarm' && this.type !== 'AccessDenied' && this.type !== 'AccessGranted' && this.type !== 'AccessOther') {
                return null;
            }
            return {
                type: this.type as MarkerType,
                position: (this.start.t - baseTime) * scale,
                data: this.start,
            };
        }
    }

    enum STATES	{
        LOOK_FOR_ALARM = 1,
        ON_CAMERA_DOWNLOAD = 2,
        CORD_CUT = 3,
        IGNORED = 4,
        LOOK_FOR_ALARM_FG = 5,
        NO_RECORD = 6,
        WRITE_BUFFER = 7,
    }

    enum EVENTS	{
        E_ALARM = 0,
        E_NOT_USED1 = 1,
        E_TOO_MUCH_CHANGE = 2,
        E_STABLE = 3,
        E_NOT_USED4 = 4,
        E_NOT_USED5 = 5,
        E_NOT_USED6 = 6,
        E_NONE = 7,
        E_CORD_CUT = 8,
        E_ZOOM_DONE = 9,
        E_SWIPE_ACCESS_GRANTED = 10,
        E_SWIPE_ACCESS_DENIED = 11,
        E_CAMERA_INPUT_ALARM = 12,
        E_CAMERA_INPUT_STABLE = 13,
        E_ZOOM_START = 14,
        E_ACCESS_OTHER = 15,
    }


    const RecordingZIndex = 0;
    const DownloadZIndex = 1;
    const CordCutZIndex = 2;
    const MotionZIndex = 3;
    const AccessZIndex = 4;
    const AlarmZIndex = 5;
    const ZoomZIndex = 6;


    // Can't use subclasses because TypeScript helper method __extends() won't be available in the worker
    // (Note: can overcome this by creating a dedicated source bundle)
    const RecordingTimelineBar = (start: CameraMessage) => new TimelineBarBuilder('Stable', RecordingZIndex, start);
    const DownloadTimelineBar = (start: CameraMessage) => new TimelineBarBuilder('CameraDownload', DownloadZIndex, start);
    const CordCutTimelineBar = (start: CameraMessage) => new TimelineBarBuilder('NoSignal', CordCutZIndex, start, 2);
    const MotionTimelineBar = (start: CameraMessage) => new TimelineBarBuilder('Motion', MotionZIndex, start, 2);
    const AccessTimelineBar = (start: CameraMessage, accessType: AccessType) => new TimelineBarBuilder(accessType, AccessZIndex, start, 2);
    const AlarmTimelineBar = (start: CameraMessage) => new TimelineBarBuilder('Alarm', AlarmZIndex, start, 2);
    const ZoomTimelineBar = (start: CameraMessage) => new TimelineBarBuilder('Zoom', ZoomZIndex, start, 0, .7, .3);

    let lastSignalUtcTime = 0;

    let pendingRecordBar: TimelineBarBuilder | null = null;
    let pendingDownloadBar: TimelineBarBuilder | null = null;
    let pendingWriteBufferBar: TimelineBarBuilder | null = null;
    let pendingMotionBar: TimelineBarBuilder | null = null;
    let pendingCordCutBar: TimelineBarBuilder | null = null;
    let pendingZoomBar: TimelineBarBuilder | null = null;
    const pendingAlarmBars: { [id: string]: TimelineBarBuilder } = {};
    const barBuilders: TimelineBarBuilder[] = [];

    function closePendingZoomBar(end: CameraMessage) {
        if (pendingZoomBar) {
            pendingZoomBar.end = end;
            barBuilders.push(pendingZoomBar);
            pendingZoomBar = null;
        }
    }

    function closePendingAlarmBar(alarmId: string) {
        const bar = pendingAlarmBars[alarmId];
        if (bar) {
            bar.end = bar.start; // Treat open CAMERA_INPUT_ALARMS as a single-fire event
            delete pendingAlarmBars[alarmId];
            barBuilders.push(bar);
        }
    }

    function closePendingBars(time: number) {
        const fakeEnd = { t: time, fake: true } as CameraMessage;
        if (pendingRecordBar) {
            pendingRecordBar.end = fakeEnd;
            barBuilders.push(pendingRecordBar);
            pendingRecordBar = null;
        }
        if (pendingMotionBar) {
            pendingMotionBar.end = fakeEnd;
            barBuilders.push(pendingMotionBar);
            pendingMotionBar = null;
        }
        if (pendingCordCutBar) {
            pendingCordCutBar.end = fakeEnd;
            barBuilders.push(pendingCordCutBar);
            pendingCordCutBar = null;
        }
        Object.keys(pendingAlarmBars).forEach(closePendingAlarmBar);
        closePendingZoomBar(fakeEnd);
    }

    let i = 0;
    while (i < messages.length) {
        const msg = messages[i++];
        const {
            e, // event
            s, // state
            a, // alarmId
            p, // policyId
            t, // time
        } = msg;

        // Supposed to get signals every 60 seconds minimum. So close off all pending bars
        // if we haven't seen anything for over two minutes -- because the Tracker or some
        // service has probably crashed, etc.
        if (t > lastSignalUtcTime + 130000) {
            closePendingBars(lastSignalUtcTime); // Assume the crash happened immediately after the last signal
        }

        // Handle recording
        if (s === STATES.LOOK_FOR_ALARM && !pendingRecordBar) {
            pendingRecordBar = RecordingTimelineBar(msg);
        }
        else if (s === STATES.NO_RECORD && pendingRecordBar) {
            pendingRecordBar.end = msg;
            barBuilders.push(pendingRecordBar);
            pendingRecordBar = null;
        }

        // Handle write buffer (pre/post alarm recording)
        if (s === STATES.WRITE_BUFFER && !pendingWriteBufferBar) {
            pendingWriteBufferBar = RecordingTimelineBar(msg);
        }
        else if (s !== STATES.WRITE_BUFFER && pendingWriteBufferBar) {
            pendingWriteBufferBar.end = msg;
            barBuilders.push(pendingWriteBufferBar);
            pendingWriteBufferBar = null;
        }

        // Handle camera download
        if (s === STATES.ON_CAMERA_DOWNLOAD && !pendingDownloadBar) {
            pendingDownloadBar = DownloadTimelineBar(msg);
        }
        else if (s !== STATES.ON_CAMERA_DOWNLOAD && pendingDownloadBar) {
            pendingDownloadBar.end = msg;
            barBuilders.push(pendingDownloadBar);
            pendingDownloadBar = null;
        }

        // Handle cord cut
        if (s === STATES.CORD_CUT && !pendingCordCutBar) {
            pendingCordCutBar = CordCutTimelineBar(msg);
        }
        else if (s !== STATES.CORD_CUT && pendingCordCutBar) {
            pendingCordCutBar.end = msg;
            barBuilders.push(pendingCordCutBar);
            pendingCordCutBar = null;
        }

        // Handle motion
        if (s === STATES.LOOK_FOR_ALARM_FG && !pendingMotionBar) {
            pendingMotionBar = MotionTimelineBar(msg);
        }
        else if (s !== STATES.LOOK_FOR_ALARM_FG && pendingMotionBar) {
            pendingMotionBar.end = msg;
            barBuilders.push(pendingMotionBar);
            pendingMotionBar = null;
        }

        // Handle PTZ movement from an alarm (Note: only manual zoom starts with E_ZOOM_START)
        if (p && autoTrackingPolicies[p]) {
            // Close off the previous bar if we've got a new autotracking policy
            if (pendingZoomBar && pendingZoomBar.start.p !== p) {
                closePendingZoomBar(msg);
            }
            if (!pendingZoomBar) {
                pendingZoomBar = ZoomTimelineBar(msg);
            }
        }

        // Handle events
        switch (e) {
            case EVENTS.E_SWIPE_ACCESS_GRANTED:
                barBuilders.push(AccessTimelineBar(msg, 'AccessGranted'));
                break;

            case EVENTS.E_SWIPE_ACCESS_DENIED:
                barBuilders.push(AccessTimelineBar(msg, 'AccessDenied'));
                break;

            case EVENTS.E_ACCESS_OTHER:
                barBuilders.push(AccessTimelineBar(msg, 'AccessOther'));
                break;

            case EVENTS.E_ALARM:
                barBuilders.push(AlarmTimelineBar(msg));
                break;

            case EVENTS.E_CAMERA_INPUT_ALARM:
                closePendingAlarmBar(a.toString());
                pendingAlarmBars[a.toString()] = AlarmTimelineBar(msg);
                break;

            case EVENTS.E_CAMERA_INPUT_STABLE:
                let pendingAlarmBar = pendingAlarmBars[a.toString()];
                if (typeof pendingAlarmBar !== 'undefined') {
                    delete pendingAlarmBars[a.toString()];
                }
                else {
                    // No start for this alarm -- it must be before our window of camera messages.
                    pendingAlarmBar = AlarmTimelineBar(msg);
                }
                pendingAlarmBar.end = msg;
                barBuilders.push(pendingAlarmBar);
                break;

            case EVENTS.E_ZOOM_START:
                closePendingZoomBar(msg);
                pendingZoomBar = ZoomTimelineBar(msg);
                break;

            case EVENTS.E_ZOOM_DONE:
                closePendingZoomBar(msg);
                break;

            default:
                break;
        }

        lastSignalUtcTime = msg.t;
    }

    closePendingBars(lastSignalUtcTime);

    return barBuilders;
}



// WEBPACK FOOTER //
// ./src/workers/timeline/timelineBarBuilder.ts